import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import api from '../services/api';

// Only these users can enable Production Readonly Mode
const ALLOWED_USERS = [
  'lee.marinelli@gwii.com',
  'john.duplechain@gwii.com',
  'lisa.maul@gwii.com',
];

interface ProductionReadonlyModeContextType {
  productionReadonlyMode: boolean;
  setProductionReadonlyMode: (enabled: boolean) => void;
  isAllowedUser: boolean;
  isValidEnvironment: boolean;
  canEnableMode: boolean;
}

const ProductionReadonlyModeContext = createContext<ProductionReadonlyModeContextType | undefined>(undefined);

export const ProductionReadonlyModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { accounts } = useMsal();
  const currentUser = accounts[0];

  // Check if current user is in allowed list (case-insensitive)
  const userEmail = currentUser?.username?.toLowerCase() || '';
  const isAllowedUser = ALLOWED_USERS.some(email => email.toLowerCase() === userEmail);

  // Check if environment is local or test (not production)
  const isValidEnvironment =
    import.meta.env.VITE_ENV === 'test' ||
    import.meta.env.VITE_ENV === 'local' ||
    import.meta.env.DEV === true;

  // Can only enable mode if both conditions are met
  const canEnableMode = isAllowedUser && isValidEnvironment;

  // State - only load from localStorage if user is allowed
  const [productionReadonlyMode, setProductionReadonlyModeState] = useState(() => {
    if (!canEnableMode) return false;
    return localStorage.getItem('productionReadonlyMode') === 'true';
  });

  // Setter with validation
  const setProductionReadonlyMode = useCallback((enabled: boolean) => {
    // Only allow enabling if user is authorized
    if (enabled && !canEnableMode) {
      console.warn('Production Readonly Mode: User not authorized to enable this mode');
      return;
    }

    setProductionReadonlyModeState(enabled);
    localStorage.setItem('productionReadonlyMode', enabled.toString());

    // Also need to notify backend to pause sync threads
    if (enabled) {
      api.post('/admin/production-readonly-mode/enable')
        .catch(() => {}); // Silently fail - middleware will still block writes
    } else {
      api.post('/admin/production-readonly-mode/disable')
        .catch(() => {});
    }
  }, [canEnableMode]);

  // Clear mode if user is no longer authorized (e.g., different user logged in)
  useEffect(() => {
    if (!canEnableMode && productionReadonlyMode) {
      setProductionReadonlyModeState(false);
      localStorage.removeItem('productionReadonlyMode');
    }
  }, [canEnableMode, productionReadonlyMode]);

  return (
    <ProductionReadonlyModeContext.Provider
      value={{
        productionReadonlyMode,
        setProductionReadonlyMode,
        isAllowedUser,
        isValidEnvironment,
        canEnableMode,
      }}
    >
      {children}
    </ProductionReadonlyModeContext.Provider>
  );
};

export const useProductionReadonlyMode = (): ProductionReadonlyModeContextType => {
  const context = useContext(ProductionReadonlyModeContext);
  if (!context) {
    throw new Error('useProductionReadonlyMode must be used within ProductionReadonlyModeProvider');
  }
  return context;
};
